/**
 * Configurações → Financeiro → Relatórios por e-mail e alertas (R5-A · L8).
 *
 * Destinatários (lista única compartilhada, com marcação diário/mensal),
 * liga/desliga por relatório e canal, alertas em tempo real (gatilhos + teto)
 * e botões "Enviar agora" (mesmo builder dos jobs — zero divergência).
 * Tudo no servidor (SQLite) — ZERO localStorage. Mobile/PWA.
 */
import { useEffect, useState } from 'react';

import { api } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, Alert, Button, Switch } from '@/components/ui';

type RelTipo = 'diario' | 'mensal';
interface Destinatario {
  id: string;
  email: string;
  relatorios: RelTipo[];
}
interface RelConfig {
  diarioEmail: boolean;
  diarioPush: boolean;
  mensalEmail: boolean;
  mensalPush: boolean;
  alertaCaixaZero: boolean;
  alertaContaGrande: boolean;
  alertaMetaEstourada: boolean;
  alertaInconformidadeCritica: boolean;
  alertaContaGrandeTetoCentavos: number;
  horarioEnvio: string;
}
interface Limiares {
  gastoPct: number;
  pisoCentavos: number;
  duplicadoJanelaDias: number;
  atipicoMult: number;
  inadimplenteMeses: number;
}
interface ConfigResp {
  destinatarios: Destinatario[];
  config: RelConfig;
  limiares?: Limiares;
}

const BASE = '/api/v1/admin/financeiro/relatorios';

export function FinanceiroRelatoriosConfigCard() {
  const [dest, setDest] = useState<Destinatario[]>([]);
  const [cfg, setCfg] = useState<RelConfig | null>(null);
  const [novoEmail, setNovoEmail] = useState('');
  const [teto, setTeto] = useState('');
  const [lim, setLim] = useState<Limiares | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [enviando, setEnviando] = useState<RelTipo | null>(null);

  function carregar() {
    Promise.resolve(api.get<ConfigResp>(`${BASE}/config`))
      .then((r) => {
        setDest(Array.isArray(r?.destinatarios) ? r.destinatarios : []);
        if (r?.config) {
          setCfg(r.config);
          setTeto((r.config.alertaContaGrandeTetoCentavos / 100).toFixed(2).replace('.', ','));
        }
        if (r?.limiares) setLim(r.limiares);
      })
      .catch((e: Error) => setErr(e.message));
  }
  useEffect(carregar, []);

  function patchCfg(patch: Partial<RelConfig>) {
    setErr(null);
    Promise.resolve(api.put<{ config: RelConfig }>(`${BASE}/config`, patch))
      .then((r) => r?.config && setCfg(r.config))
      .catch((e: Error) => setErr(e.message));
  }

  function salvarTeto() {
    const n = parseFloat(teto.trim().replace(/\./g, '').replace(',', '.'));
    if (!Number.isFinite(n) || n <= 0) {
      setErr('Valor do teto inválido.');
      return;
    }
    patchCfg({ alertaContaGrandeTetoCentavos: Math.round(n * 100) });
    setMsg('Teto salvo.');
  }

  function addEmail() {
    setErr(null);
    setMsg(null);
    Promise.resolve(api.post<{ destinatario?: Destinatario; error?: string }>(`${BASE}/destinatarios`, { email: novoEmail }))
      .then(() => {
        setNovoEmail('');
        carregar();
      })
      .catch((e: Error) =>
        setErr(e.message.includes('email_invalido') ? 'E-mail inválido.' : e.message.includes('ja_cadastrado') ? 'Esse e-mail já está na lista.' : e.message),
      );
  }

  function toggleRel(d: Destinatario, tipo: RelTipo) {
    const rel = d.relatorios.includes(tipo)
      ? d.relatorios.filter((r) => r !== tipo)
      : [...d.relatorios, tipo];
    Promise.resolve(api.put(`${BASE}/destinatarios/${d.id}`, { relatorios: rel }))
      .then(carregar)
      .catch((e: Error) => setErr(e.message));
  }

  function remover(id: string) {
    Promise.resolve(api.delete(`${BASE}/destinatarios/${id}`))
      .then(carregar)
      .catch((e: Error) => setErr(e.message));
  }

  function enviarAgora(tipo: RelTipo) {
    setErr(null);
    setMsg(null);
    setEnviando(tipo);
    Promise.resolve(
      api.post<{ emailEnviados: number; emailFalhas: number; emailStub: boolean; destinatarios: string[] }>(
        `${BASE}/enviar-agora`,
        { tipo },
      ),
    )
      .then((r) => {
        if (r?.emailStub) setMsg('Envio em modo teste (e-mail do servidor não configurado) — nada foi entregue.');
        else if ((r?.emailFalhas ?? 0) > 0) setErr(`Enviado com falhas: ${r?.emailEnviados ?? 0} ok, ${r?.emailFalhas} falhou(aram).`);
        else setMsg(`Relatório ${tipo === 'diario' ? 'diário' : 'mensal'} enviado pra ${r?.emailEnviados ?? 0} destinatário(s).`);
      })
      .catch((e: Error) =>
        setErr(e.message.includes('aguarde') || e.message.includes('429') ? 'Aguarde 1 minuto entre envios.' : `Não deu certo: ${e.message}`),
      )
      .finally(() => setEnviando(null));
  }

  if (!cfg) {
    return (
      <Card className="mb-6">
        <CardContent className="py-6 text-sm text-ink-3 dark:text-ink-dark-3">
          {err ? `Erro ao carregar: ${err}` : 'Carregando…'}
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="mb-6" data-testid="fin-relatorios-config">
      <CardHeader>
        <CardTitle>Relatórios por e-mail e alertas</CardTitle>
        <CardDescription>
          Resumo diário (07:30) e fechamento mensal (dia 1º, 07:29) por e-mail e notificação, mais
          alertas na hora quando algo estoura.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {err && <Alert variant="danger" rail>{err}</Alert>}
        {msg && <Alert variant="info" rail>{msg}</Alert>}

        {/* Horário do envio (13/08) — o mensal segue no dia 1º, 1 min antes. */}
        <div className="rounded-xl border border-line dark:border-line-dark p-3.5 flex items-center justify-between gap-3">
          <div>
            <div className="font-medium text-sm text-ink-1 dark:text-ink-dark-1">Horário do envio</div>
            <div className="text-xs text-ink-3 dark:text-ink-dark-3">
              Diário todo dia neste horário; o fechamento mensal sai no dia 1º, um minuto antes.
              Se o valor for inválido, o sistema envia às 07:30.
            </div>
          </div>
          <input
            type="time"
            data-testid="horario-envio"
            value={cfg?.horarioEnvio ?? '07:30'}
            onChange={(e) => {
              if (/^([01]\d|2[0-3]):[0-5]\d$/.test(e.target.value)) patchCfg({ horarioEnvio: e.target.value });
            }}
            className="h-9 rounded-lg border border-line-soft dark:border-line-soft-dark bg-paper-warm dark:bg-paper-dark-surface px-2 text-sm text-ink-1 dark:text-ink-dark-1"
          />
        </div>

        {/* Toggles por relatório */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {(
            [
              { rot: 'Relatório diário', email: 'diarioEmail', push: 'diarioPush', tipo: 'diario' },
              { rot: 'Fechamento mensal', email: 'mensalEmail', push: 'mensalPush', tipo: 'mensal' },
            ] as Array<{ rot: string; email: keyof RelConfig; push: keyof RelConfig; tipo: RelTipo }>
          ).map((r) => (
            <div key={r.tipo} className="rounded-xl border border-line dark:border-line-dark p-3.5">
              <div className="font-medium text-sm text-ink-1 dark:text-ink-dark-1 mb-2">{r.rot}</div>
              <label className="flex items-center justify-between text-sm text-ink-2 dark:text-ink-dark-2 py-1">
                <span>E-mail</span>
                <Switch checked={Boolean(cfg[r.email])} onChange={(e) => patchCfg({ [r.email]: e.target.checked })} />
              </label>
              <label className="flex items-center justify-between text-sm text-ink-2 dark:text-ink-dark-2 py-1">
                <span>Notificação no app</span>
                <Switch checked={Boolean(cfg[r.push])} onChange={(e) => patchCfg({ [r.push]: e.target.checked })} />
              </label>
              <Button
                size="sm"
                variant="outline"
                className="mt-2 w-full"
                loading={enviando === r.tipo}
                disabled={enviando !== null}
                onClick={() => enviarAgora(r.tipo)}
                data-testid={`enviar-agora-${r.tipo}`}
              >
                Enviar agora
              </Button>
            </div>
          ))}
        </div>

        {/* Destinatários */}
        <div>
          <div className="font-medium text-sm text-ink-1 dark:text-ink-dark-1 mb-1">Quem recebe os e-mails</div>
          <p className="text-xs text-ink-3 dark:text-ink-dark-3 mb-2">
            A lista vale pros dois relatórios — desmarque um deles se a pessoa só deve receber o outro.
          </p>
          <div className="space-y-1.5">
            {dest.map((d) => (
              <div key={d.id} className="flex flex-wrap items-center gap-3 rounded-lg border border-line dark:border-line-dark px-3 py-2 text-sm">
                <span className="flex-1 min-w-[160px] text-ink-1 dark:text-ink-dark-1 break-all">{d.email}</span>
                <label className="flex items-center gap-1.5 text-xs text-ink-2 dark:text-ink-dark-2">
                  <input type="checkbox" checked={d.relatorios.includes('diario')} onChange={() => toggleRel(d, 'diario')} className="accent-red" />
                  Diário
                </label>
                <label className="flex items-center gap-1.5 text-xs text-ink-2 dark:text-ink-dark-2">
                  <input type="checkbox" checked={d.relatorios.includes('mensal')} onChange={() => toggleRel(d, 'mensal')} className="accent-red" />
                  Mensal
                </label>
                <button onClick={() => remover(d.id)} className="text-xs text-red hover:underline" aria-label={`Remover ${d.email}`}>
                  Remover
                </button>
              </div>
            ))}
            {dest.length === 0 && <div className="text-xs text-ink-3 dark:text-ink-dark-3">Nenhum destinatário — adicione um e-mail abaixo.</div>}
          </div>
          <div className="flex gap-2 mt-2">
            <input
              type="email"
              value={novoEmail}
              onChange={(e) => setNovoEmail(e.target.value)}
              placeholder="email@exemplo.com.br"
              className="flex-1 min-h-[40px] bg-paper-surface dark:bg-paper-dark-surface border border-line dark:border-line-dark rounded-lg px-3 text-sm text-ink-1 dark:text-ink-dark-1"
              data-testid="novo-destinatario"
            />
            <Button size="sm" onClick={addEmail} disabled={!novoEmail.trim()}>
              Adicionar
            </Button>
          </div>
        </div>

        {/* Alertas em tempo real */}
        <div>
          <div className="font-medium text-sm text-ink-1 dark:text-ink-dark-1 mb-1">Alertas na hora (notificação no app)</div>
          <p className="text-xs text-ink-3 dark:text-ink-dark-3 mb-2">
            Avisos imediatos quando o dado sincroniza — no máximo 1 vez por dia pra cada situação.
          </p>
          {(
            [
              { k: 'alertaCaixaZero', rot: 'Caixa projetado fura o zero' },
              { k: 'alertaContaGrande', rot: 'Conta grande vencendo em até 2 dias' },
              { k: 'alertaMetaEstourada', rot: 'Meta estourada (teto de despesa / resultado)' },
              { k: 'alertaInconformidadeCritica', rot: 'Novo ponto de atenção crítico' },
            ] as Array<{ k: keyof RelConfig; rot: string }>
          ).map((a) => (
            <label key={a.k} className="flex items-center justify-between text-sm text-ink-2 dark:text-ink-dark-2 py-1">
              <span>{a.rot}</span>
              <Switch checked={Boolean(cfg[a.k])} onChange={(e) => patchCfg({ [a.k]: e.target.checked })} />
            </label>
          ))}
          <div className="flex items-center gap-2 mt-2 text-sm text-ink-2 dark:text-ink-dark-2">
            <span>Conta grande = acima de R$</span>
            <input
              type="text"
              inputMode="decimal"
              value={teto}
              onChange={(e) => setTeto(e.target.value)}
              className="w-28 min-h-[36px] bg-paper-surface dark:bg-paper-dark-surface border border-line dark:border-line-dark rounded-lg px-2 text-sm text-ink-1 dark:text-ink-dark-1"
              data-testid="teto-conta-grande"
            />
            <Button size="sm" variant="outline" onClick={salvarTeto}>
              Salvar teto
            </Button>
          </div>
        </div>

        {/* R5-B: limiares do radar de inconformidades */}
        {lim && (
          <div data-testid="limiares-radar">
            <div className="font-medium text-sm text-ink-1 dark:text-ink-dark-1 mb-1">
              Radar de inconformidades (limiares)
            </div>
            <p className="text-xs text-ink-3 dark:text-ink-dark-3 mb-2">
              Ajuste quando o radar considera algo fora do padrão. Vale pra aba Inconformidades, pro
              resumo diário e pros alertas.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-sm text-ink-2 dark:text-ink-dark-2">
              {(
                [
                  { k: 'gastoPct', rot: 'Gasto acima da média (+%)', sufixo: '% sobre a média de 3 meses' },
                  { k: 'pisoCentavos', rot: 'Piso de análise (R$)', sufixo: 'abaixo disso não analisa', emReais: true },
                  { k: 'duplicadoJanelaDias', rot: 'Duplicado: janela (dias)', sufixo: 'mesmo fornecedor + valor' },
                  { k: 'atipicoMult', rot: 'Valor atípico (× a média)', sufixo: 'vs 6 meses do fornecedor' },
                  { k: 'inadimplenteMeses', rot: 'Inadimplente: meses com atraso', sufixo: 'dentro dos últimos 6' },
                ] as Array<{ k: keyof Limiares; rot: string; sufixo: string; emReais?: boolean }>
              ).map((c) => (
                <label key={c.k} className="flex items-center justify-between gap-2">
                  <span>
                    {c.rot}
                    <span className="block text-xs text-ink-3 dark:text-ink-dark-3">{c.sufixo}</span>
                  </span>
                  <input
                    type="number"
                    step={c.k === 'atipicoMult' ? 0.5 : 1}
                    value={c.emReais ? lim[c.k] / 100 : lim[c.k]}
                    onChange={(e) => {
                      const v = Number(e.target.value);
                      if (!Number.isFinite(v) || v <= 0) return;
                      const novo = { ...lim, [c.k]: c.emReais ? Math.round(v * 100) : v };
                      setLim(novo);
                      Promise.resolve(api.put(`${BASE}/limiares`, { [c.k]: novo[c.k] })).catch((er: Error) =>
                        setErr(er.message),
                      );
                    }}
                    className="w-24 min-h-[36px] bg-paper-surface dark:bg-paper-dark-surface border border-line dark:border-line-dark rounded-lg px-2 text-sm text-ink-1 dark:text-ink-dark-1 text-center"
                  />
                </label>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
